import {
  Autocomplete,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Checkbox,
  IconButton,
} from "@mui/material";
import { DataGrid, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { useAuth } from "src/context/auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, ChangeEvent } from "react";
import {
  fetchSmsList,
  deleteBulkSms,
  FollowupScheduleSms,
  SmsFilter,
} from "src/api/followup-schedule-sms";
import { BootstrapTooltip } from "src/utilities";
import { useNotifications } from "@toolpad/core/useNotifications";
import DeleteIcon from "@mui/icons-material/Delete";

const statusLabels: Record<number, string> = {
  0: "در صف",
  1: "ارسال شد",
  2: "خطا",
  3: "رد شد",
  4: "لغو شد",
};

export default function SmsList() {
  const { user } = useAuth();
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [filters, setFilters] = useState<SmsFilter>({});
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const queryKey = useMemo(
    () => ["sms-list", paginationModel.page, paginationModel.pageSize, filters],
    [paginationModel, filters]
  );

  const { data, isLoading, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      fetchSmsList(user?.token || "", {
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        ...filters,
      }),
    enabled: !!user?.token,
  });

  const rows: FollowupScheduleSms[] = data?.items || [];
  const totalCount = data?.totalCount || 0;

  useEffect(() => {
    if (!user?.token) return;
    refetch();
  }, [user?.token]);

  const deleteMutation = useMutation({
    mutationFn: (smsIds: number[]) => deleteBulkSms(user?.token || "", smsIds),
    onSuccess: (data) => {
      notifications.show(data.message || "پیامک‌ها با موفقیت حذف شدند", {
        severity: "success",
        autoHideDuration: 3000,
      });
      setSelectedRows([]);
      setOpenDeleteDialog(false);
      queryClient.invalidateQueries({ queryKey: ["sms-list"] });
    },
    onError: (error: Error) => {
      notifications.show(error.message || "خطا در حذف پیامک‌ها", {
        severity: "error",
        autoHideDuration: 4000,
      });
    },
  });

  const handleDeleteClick = () => {
    if (selectedRows.length === 0) {
      notifications.show("لطفاً حداقل یک پیامک را انتخاب کنید", {
        severity: "warning",
        autoHideDuration: 3000,
      });
      return;
    }
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    const smsIds = selectedRows.map((id) => Number(id));
    deleteMutation.mutate(smsIds);
  };

  const columns: GridColDef[] = [
    { field: "scheduleTitle", headerName: "عنوان پیگیری", width: 160 },
    { field: "groupTitle", headerName: "گروه پیگیری", width: 150 },
    { field: "serviceName", headerName: "خدمت", width: 160 },
    { field: "customerName", headerName: "مشتری", width: 170 },
    {
      field: "sendAt",
      headerName: "زمان ارسال",
      width: 170,
      valueFormatter: (params: any) => {
        const value = params?.value;
        return value ? new Date(value).toLocaleString("fa-IR") : "-";
      },
    },
    // { field: "message", headerName: "متن", flex: 1, minWidth: 200 },
    {
      field: "status",
      headerName: "وضعیت",
      width: 110,
      valueFormatter: (params: any) => {
        const value = params?.value;
        return value !== undefined && value !== null
          ? (statusLabels[value] ?? value)
          : "-";
      },
    },
    {
      field: "sentOn",
      headerName: "ارسال شده در",
      width: 170,
      valueFormatter: (params: any) => {
        const value = params?.value;
        return value ? new Date(value).toLocaleString("fa-IR") : "-";
      },
    },
  ];

  const handleFilterChange =
    (key: keyof SmsFilter) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFilters((prev) => ({
        ...prev,
        [key]: e.target.value || undefined,
      }));
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

  const uniqueOptions = useMemo(() => {
    const result = {
      followupIds: Array.from(new Set(rows.map((r) => r.followupScheduleRef))),
      groupTitles: Array.from(
        new Set(rows.map((r) => r.groupTitle).filter(Boolean))
      ),
      serviceNames: Array.from(
        new Set(rows.map((r) => r.serviceName).filter(Boolean))
      ),
      agreements: Array.from(
        new Set(rows.map((r) => r.agreementRef).filter(Boolean))
      ),
      customers: Array.from(
        new Set(rows.map((r) => r.customerName).filter(Boolean))
      ),
    };
    return result;
  }, [rows]);

  return (
    <Box p={2}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5">پیامک‌های پیگیری دوره‌ای</Typography>
        {selectedRows.length > 0 && (
          <BootstrapTooltip
            title={`حذف ${selectedRows.length} پیامک انتخاب شده`}
          >
            <IconButton
              color="error"
              onClick={handleDeleteClick}
              disabled={deleteMutation.isPending}
            >
              <DeleteIcon />
            </IconButton>
          </BootstrapTooltip>
        )}
      </Box>

      <Grid container spacing={2} mb={2}>
        {/* <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            size="small"
            options={uniqueOptions.followupIds}
            getOptionLabel={(o) => (o ? String(o) : "")}
            value={filters.followupScheduleId ?? null}
            onChange={(_, v) =>
              setFilters((prev) => ({
                ...prev,
                followupScheduleId: v ?? undefined,
              }))
            }
            renderInput={(params) => (
              <TextField {...params} label="شناسه پیگیری دوره‌ای" fullWidth />
            )}
          />
        </Grid> */}
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            size="small"
            options={uniqueOptions.groupTitles}
            value={
              filters.groupRef !== undefined && filters.groupRef !== null
                ? String(filters.groupRef)
                : null
            }
            onInputChange={(_, v) =>
              setFilters((prev) => ({
                ...prev,
                groupRef: v ? Number(v) : undefined,
              }))
            }
            freeSolo
            renderInput={(params) => (
              <TextField {...params} label="گروه پیگیری" fullWidth />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            size="small"
            options={uniqueOptions.serviceNames}
            value={
              filters.serviceRef !== undefined && filters.serviceRef !== null
                ? String(filters.serviceRef)
                : null
            }
            onInputChange={(_, v) =>
              setFilters((prev) => ({
                ...prev,
                serviceRef: v ? Number(v) : undefined,
              }))
            }
            freeSolo
            renderInput={(params) => (
              <TextField {...params} label="خدمت" fullWidth />
            )}
          />
        </Grid>
        {/* <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            size="small"
            options={uniqueOptions.agreements}
            getOptionLabel={(o) => (o ? String(o) : "")}
            value={filters.agreementRef ?? null}
            onChange={(_, v) =>
              setFilters((prev) => ({
                ...prev,
                agreementRef: v ?? undefined,
              }))
            }
            renderInput={(params) => (
              <TextField {...params} label="شناسه قرارداد" fullWidth />
            )}
          />
        </Grid> */}
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            size="small"
            options={uniqueOptions.customers}
            value={filters.customerName ?? null}
            onChange={(_, v) =>
              setFilters((prev) => ({
                ...prev,
                customerName: v ?? undefined,
              }))
            }
            freeSolo
            renderInput={(params) => (
              <TextField {...params} label="نام مشتری" fullWidth />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <BootstrapTooltip title="اعمال فیلترها">
            <Button variant="contained" onClick={() => refetch()}>
              جستجو
            </Button>
          </BootstrapTooltip>
        </Grid>
      </Grid>

      <div style={{ height: 650, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          checkboxSelection
          disableRowSelectionOnClick
          pageSizeOptions={[10, 20, 50]}
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => setPaginationModel(model)}
          onRowSelectionModelChange={(newSelection) =>
            setSelectedRows(newSelection)
          }
          rowSelectionModel={selectedRows}
          loading={isLoading}
          getRowId={(row) => row.id}
        />
      </div>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>تایید حذف</DialogTitle>
        <DialogContent>
          <DialogContentText>
            آیا از حذف {selectedRows.length} پیامک انتخاب شده اطمینان دارید؟ این
            عمل قابل بازگشت نیست.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            disabled={deleteMutation.isPending}
          >
            انصراف
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? "در حال حذف..." : "حذف"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
